import { useMutation } from '@connectrpc/connect-query'
import { useUser } from '@descope/react-sdk'
import type { CommentBody } from '@liveblocks/client'
import { useCallback, useMemo } from 'react'

import { useListUsers } from '@/api/list-users.hook'
import { CommentNotificationType } from '@/gen/notifications/v1/model_pb'
import { sendCommentNotification } from '@/gen/notifications/v1/notifications_service-NotificationsService_connectquery'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'

import { serializeCommentBody } from './serialize-comment-body'
import { buildEmailToUserId, extractMentionEmails, mapEmailsToUserIds } from './utils'

type SendInput = {
  assessmentId: string | undefined
  body: CommentBody
  threadId?: string
  commentId?: string
  // Absent for page-level assessment comments, which the RPC allows.
  controlId?: string
  participantEmails?: string[]
  trackingProps?: EventProperties
}

export const useSendCommentNotification = () => {
  const { user } = useUser()
  const { data: users } = useListUsers()
  const emailToUserId = useMemo(() => buildEmailToUserId(users), [users])
  const { mutate } = useMutation(sendCommentNotification)
  const trackMentionUsed = useTrackCallback('assessment.comment.mention.used')

  return useCallback(
    async ({
      assessmentId,
      body,
      threadId,
      commentId,
      controlId,
      participantEmails = [],
      trackingProps,
    }: SendInput) => {
      const toUserIds = (emails: string[]) => mapEmailsToUserIds(emails, emailToUserId, user?.email)

      const mentioned = toUserIds(extractMentionEmails(body))
      if (mentioned.length > 0) {
        trackMentionUsed({
          ...trackingProps,
          mentioned_user_ids: mentioned,
          mention_count: mentioned.length,
          control_id: controlId,
          assessment_id: assessmentId,
        })
      }

      // A mention wins, so one comment never sends both notifications to the same person.
      const replied = toUserIds(participantEmails).filter((id) => !mentioned.includes(id))

      if (!assessmentId || (mentioned.length === 0 && replied.length === 0)) return

      // One id for both sends, so a mention and a reply are not attributed to different comments.
      const shared = {
        assessmentId,
        controlId,
        threadId,
        commentId: commentId ?? crypto.randomUUID(),
        commentBody: (await serializeCommentBody(body)).trim(),
      }

      const send = (type: CommentNotificationType, recipientUserIds: string[]) => {
        if (recipientUserIds.length > 0) mutate({ ...shared, type, recipientUserIds })
      }

      send(CommentNotificationType.MENTION, mentioned)
      send(CommentNotificationType.REPLY, replied)
    },
    [emailToUserId, user?.email, mutate, trackMentionUsed],
  )
}
